/**
 * Recognising a blank `booth2` window opened only because the app was started twice.
 *
 * From v2.4.12 to v2.10.4 a second launch (a double click, or the Windows login
 * item racing a Startup-folder shortcut) relaunched itself as `booth2`: a separate
 * profile with its own login, licence binding, printer and camera. To an operator
 * that reads as "the booth reset itself and wants me to log in again" (monopixbooth,
 * and 11 owners in the 09-12..25 booth logs). v2.10.5 (cb98ef5f) asks first, but a
 * shell fix reaches a booth only when it updates. The renderer is served from the
 * web and reaches every booth on its next load, so it recognises that window itself.
 *
 * Only a booth2 nobody ever set up is held back. A deliberate second booth on this
 * PC has been set up, so it boots exactly as before, and "Use this as a second booth"
 * on the hold screen is remembered for a new one.
 *
 * And only while the primary booth is demonstrably running. booth2 can outlive it
 * (the primary crashed after the second launch), or start without it (auto-launch
 * switched on from inside booth2 registers the login item with `--profile=booth2`).
 * Saying "already open" then would be false, and closing would leave no booth at
 * all. A running main process writes its log every few seconds (p99.9 gap 5 s over
 * two full booth days; every gap over a minute was the app not running), so the
 * primary counts as running only if its log for today was written within a minute.
 */

/** First shell that relaunches a second launch as `booth2`. */
export const AUTO_PROFILE_FROM = '2.4.12'
/** First shell that asks before opening `booth2`: any booth2 it runs was chosen. */
export const ASKS_FIRST_FROM = '2.10.5'
/**
 * First shell whose losing duplicate launch exits before it can open a window
 * (341b17d5). Older shells open a window on the profile the running booth holds.
 */
export const DEAD_PROFILE_FIXED_FROM = '2.4.10'

/** localStorage key: the operator chose to keep this window as a second booth. */
export const KEPT_AS_SECOND_BOOTH_KEY = 'duplicate-launch:kept-as-second-booth'

export type DuplicateLaunchVerdict =
  | { kind: 'none' }
  /** A never-set-up auto profile (`booth2`…) started by a second launch. */
  | { kind: 'blank-second-profile'; profile: string }
  /** A window on a pre-2.4.10 shell that cannot read the profile the running booth holds. */
  | { kind: 'dead-profile' }

export const NO_DUPLICATE: DuplicateLaunchVerdict = { kind: 'none' }

export interface DuplicateLaunchFacts {
  /** The shell's version (`get-app-version`), or null when it could not be read. */
  shellVersion: string | null
  /** The main process's log directory, `<userData>\logs`, or null. */
  logDirectory: string | null
  /** This profile was set up at some point: licence, camera, printer, project or login. */
  profileConfigured: boolean
  /** The operator already chose to keep this window as a second booth. */
  keptAsSecondBooth: boolean
}

function parseVersion(version: string): [number, number, number] | null {
  const match = version.trim().match(/^v?(\d+)\.(\d+)\.(\d+)/)
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null
}

/** True when `version` sorts before `reference`. Unparseable input is never "before". */
export function isVersionBefore(version: string, reference: string): boolean {
  const a = parseVersion(version)
  const b = parseVersion(reference)
  if (!a || !b) return false
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return a[i] < b[i]
  }
  return false
}

/**
 * The auto-assigned profile this window runs in, read off its log directory:
 * `%APPDATA%\dreambooth-app-booth2\logs` → `booth2`. The primary profile's
 * directory carries no suffix, and an operator's own `--profile=<name>` is not a
 * `booth<N>` name, so both read as null.
 */
export function autoProfileFromLogDirectory(logDirectory: string | null): string | null {
  if (!logDirectory) return null
  const parts = logDirectory.split(/[\\/]+/).filter(Boolean)
  if (parts.length < 2 || parts[parts.length - 1].toLowerCase() !== 'logs') return null
  const match = parts[parts.length - 2].match(/-(booth\d+)$/i)
  return match ? match[1].toLowerCase() : null
}

function readPersistedState(
  storage: Pick<Storage, 'getItem'>,
  key: string
): Record<string, unknown> | null {
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: unknown }
    return parsed && typeof parsed.state === 'object' && parsed.state
      ? (parsed.state as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/**
 * Whether anyone ever set this profile up. Freshness cannot answer that: zustand
 * writes the default store back on its first load, so from the second launch on
 * an untouched booth2 looks exactly as "not fresh" as a working booth.
 */
export function readProfileConfigured(storage: Pick<Storage, 'getItem'>): boolean {
  const settings = readPersistedState(storage, 'settings-v2')
  const auth = readPersistedState(storage, 'auth-storage')
  return Boolean(
    settings?.activationCode ||
      settings?.lastSelectedCamera ||
      settings?.selectedCamera ||
      settings?.selectedPrinter ||
      settings?.defaultProjectId ||
      settings?.defaultHubId ||
      auth?.isAuthenticated
  )
}

/** Decide whether this window is a blank booth2 opened by starting the app twice. */
/** The primary counts as running only if its log was written this recently. */
export const PRIMARY_ALIVE_WINDOW_MS = 60_000

/**
 * The primary profile's log for today, derived from this booth2 window's own:
 * `…\Roaming\dreambooth-app-booth2\logs\dreambooth-2026-10-01[-<ts>].log` →
 * `…\Roaming\dreambooth-app\logs\dreambooth-2026-10-01.log`. A primary that has
 * rotated to a `-<ts>` file stops writing this one, which reads as "not running" —
 * the safe direction: booth2 then boots as it always did.
 */
export function primaryLogFileFor(ownTodayFile: string | null, profile: string): string | null {
  if (!ownTodayFile) return null
  const sep = ownTodayFile.includes('\\') ? '\\' : '/'
  const parts = ownTodayFile.split(/[\\/]/)
  if (parts.length < 3 || parts[parts.length - 2].toLowerCase() !== 'logs') return null
  const userDataDir = parts[parts.length - 3]
  const suffix = `-${profile}`
  if (!userDataDir.toLowerCase().endsWith(suffix)) return null
  const date = parts[parts.length - 1].match(/^dreambooth-(\d{4}-\d{2}-\d{2})(?:-\d+)?\.log$/)
  if (!date) return null
  return [
    ...parts.slice(0, -3),
    userDataDir.slice(0, -suffix.length),
    parts[parts.length - 2],
    `dreambooth-${date[1]}.log`
  ].join(sep)
}

/** Time of the last `[2026-10-01T02:42:37.101Z] …` line in a main-process log. */
export function lastLogTimestamp(content: string | null): number | null {
  if (!content) return null
  let end = content.length
  while (end > 0) {
    const start = content.lastIndexOf('\n', end - 1) + 1
    const match = content.slice(start, end).match(/^\[(\d{4}-\d{2}-\d{2}T[\d:.]+Z)\]/)
    if (match) {
      const at = Date.parse(match[1])
      return Number.isNaN(at) ? null : at
    }
    end = start - 1
  }
  return null
}

/** Within the window either way, so a slightly fast clock does not count as stale. */
export function isRecentLogWrite(lastAt: number | null, now: number): boolean {
  return lastAt !== null && Math.abs(now - lastAt) <= PRIMARY_ALIVE_WINDOW_MS
}

/**
 * Decide whether this window is a blank booth2 opened by starting the app twice
 * while the primary booth is running. `primaryLastLogAt` is only asked once the
 * cheap checks say "blank booth2", so a working booth never reads a log file.
 */
export async function detectDuplicateLaunch(
  facts: DuplicateLaunchFacts,
  primaryLastLogAt: (profile: string) => Promise<number | null>,
  now: () => number = Date.now
): Promise<DuplicateLaunchVerdict> {
  const version = facts.shellVersion
  if (!version || !parseVersion(version)) return NO_DUPLICATE
  if (isVersionBefore(version, AUTO_PROFILE_FROM) || !isVersionBefore(version, ASKS_FIRST_FROM)) {
    return NO_DUPLICATE
  }
  if (facts.profileConfigured || facts.keptAsSecondBooth) return NO_DUPLICATE

  const profile = autoProfileFromLogDirectory(facts.logDirectory)
  if (!profile) return NO_DUPLICATE
  if (!isRecentLogWrite(await primaryLastLogAt(profile), now())) return NO_DUPLICATE
  return { kind: 'blank-second-profile', profile }
}

/*
 * Shells older than v2.4.10 have a second failure mode for the same double start.
 * The launch that loses the single-instance lock (v2.1.8+; before that there is no
 * lock at all) still opens a full booth window on the SAME profile. Chromium cannot
 * take the storage lock the running booth holds, so this window's localStorage
 * starts empty and is never written to disk: no activation code, no login, no
 * camera or printer, and a fresh deviceId on every such launch. Operators read it
 * as "the booth reset itself" and re-enter the activation code, which moves the
 * licence onto a deviceId that is gone at the next start, so the real booth looks
 * reset next time too. Memoria (2.3.2) did this on 09-29, 10-02 and 10-06.
 *
 * Reproduced on Electron 24.8.8 with two instances on one userData. The window that
 * lost the lock saw an empty localStorage even though the profile on disk held
 * settings, in every run. IndexedDB and navigator.storage.estimate() misbehaved in
 * some runs and were normal in others, so they are not used. The lost-lock line
 * cannot be read either: these shells start their log file only after the lock
 * check, so it never reaches the log.
 *
 * What does survive is main's SQLite file, which this window's main process reads
 * independently of Chromium storage. A window whose own store is blank on a PC
 * whose SQLite remembers an activation or past sessions is looking at a profile it
 * cannot read. A genuine first install has nothing in SQLite and boots as before.
 */

export interface DeadProfileFacts {
  /** The shell's version (`get-app-version`), or null when it could not be read. */
  shellVersion: string | null
  /** This window's own localStorage shows the profile was set up. */
  profileConfigured: boolean
}

/**
 * Decide whether this window is a pre-2.4.10 duplicate launch that cannot read
 * its own profile. `mainRemembersSetup` is only asked once the cheap checks pass,
 * so a working booth never queries SQLite here.
 */
export async function detectDeadProfileWindow(
  facts: DeadProfileFacts,
  mainRemembersSetup: () => Promise<boolean>
): Promise<DuplicateLaunchVerdict> {
  const version = facts.shellVersion
  if (!version || !parseVersion(version)) return NO_DUPLICATE
  if (!isVersionBefore(version, DEAD_PROFILE_FIXED_FROM)) return NO_DUPLICATE
  if (facts.profileConfigured) return NO_DUPLICATE
  try {
    return (await mainRemembersSetup()) ? { kind: 'dead-profile' } : NO_DUPLICATE
  } catch {
    return NO_DUPLICATE
  }
}

/** Main's SQLite remembers this PC being used: an activation, or sessions it served. */
export function sqliteRemembersSetup(
  subscription: { activationCode?: string | null } | null,
  localSessionCount: number
): boolean {
  return Boolean(subscription?.activationCode) || localSessionCount > 0
}
